/**
 * The state container behind a chat client.
 *
 * Hosts render from a snapshot and nothing else, so two properties matter
 * more than they look:
 *
 * - Every change produces a **new** state object. A host that compares by
 *   reference (React, Vue, and anything memoising a render) sees the change.
 *   Mutating in place would leave those hosts showing stale messages.
 * - Subscribing calls the listener **immediately** with the current state, so
 *   a host that mounts after `open()` has already run still paints correctly.
 *   Without that, a late subscriber shows an empty chat until the next event.
 *
 * Deliberately not an event emitter. The contract is "everything renderable
 * is in the snapshot"; an emitter invites hosts to keep their own copy of
 * half the state, which is how they drift out of sync.
 */

export type Listener<T> = (state: T) => void;
export type Unsubscribe = () => void;

export interface Store<T> {
  getState(): T;
  subscribe(listener: Listener<T>): Unsubscribe;
  /** Shallow-merges a patch and notifies. No-op if nothing actually changed. */
  setState(patch: Partial<T>): void;
  /** For updates that depend on the current value. */
  update(fn: (state: T) => Partial<T>): void;
}

/**
 * `derive`, when given, runs after every patch and returns fields computed
 * from the whole state. For flags that several writers would otherwise each
 * have to keep right (see ChatState.showContactForm).
 */
export function createStore<T extends object>(initial: T, derive?: (state: T) => Partial<T>): Store<T> {
  let state = derive ? { ...initial, ...derive(initial) } : initial;
  let listeners: Listener<T>[] = [];

  const notify = () => {
    // Iterate a copy: a listener may unsubscribe itself, and splicing the
    // live array mid-loop would skip whoever follows it.
    for (const listener of [...listeners]) {
      listener(state);
    }
  };

  const changed = (patch: Partial<T>): boolean => {
    for (const key of Object.keys(patch) as (keyof T)[]) {
      if (!Object.is(state[key], patch[key])) return true;
    }
    return false;
  };

  const store: Store<T> = {
    getState: () => state,

    subscribe(listener) {
      listeners.push(listener);
      listener(state);

      let active = true;
      return () => {
        // Guard against a double unsubscribe removing someone else's
        // listener when the same function was registered twice.
        if (!active) return;
        active = false;
        const i = listeners.indexOf(listener);
        if (i !== -1) listeners.splice(i, 1);
      };
    },

    setState(patch) {
      if (!changed(patch)) return;
      const next = { ...state, ...patch };
      state = derive ? { ...next, ...derive(next) } : next;
      notify();
    },

    update(fn) {
      store.setState(fn(state));
    },
  };

  return store;
}
